import { ref, computed } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import algoliasearch from 'algoliasearch/lite'

const searchClient = algoliasearch(
  import.meta.env.VITE_ALGOLIA_APP_ID,
  import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY
)

export function useAlgoliaSearch<T>(indexName: string, numberPerPage: number = 25) {
  const index = searchClient.initIndex(indexName)

  const queryText = ref('')
  const currentPage = ref(1)
  const perPage = ref(numberPerPage)

  const searchParams = computed(() => {
    const base: Record<string, any> = {
      query: queryText.value,
      page: currentPage.value - 1,
      hitsPerPage: perPage.value,
    }
    return base
  })

  const {
    data: searchResults,
    isPending,
    isError,
    error,
    isPlaceholderData,
  } = useQuery({
    queryKey: [
      'algoliaSearch',
      indexName,
      queryText,
      currentPage,
      perPage,
    ],
    queryFn: async () => {
      const params = searchParams.value
      const result = await index.search<T>(queryText.value, params)

      return {
        hits: result.hits,
        total: result.nbHits,
        pages: result.nbPages,
      }
    },
    placeholderData: keepPreviousData,
  })

  const results = computed(() => searchResults.value?.hits ?? [])
  const totalRecords = computed(() => searchResults.value?.total ?? 0)
  const totalPages = computed(() => searchResults.value?.pages ?? 1)

  const search = (text: string) => {
    queryText.value = text
    currentPage.value = 1
  }

  const nextPage = () => {
    if (currentPage.value < totalPages.value) currentPage.value++
  }

  const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--
  }

  return {
    results,
    isPending,
    isError,
    error,
    isPlaceholderData,
    queryText,
    search,
    currentPage,
    perPage,
    totalPages,
    totalRecords,
    nextPage,
    prevPage,
  }
}
